import {
  closestCenter,
  type ClientRect,
  type Collision,
  type CollisionDetection,
} from '@dnd-kit/core'
import type { FolderSchema } from '../api/contracts'
import { isWithinFolder } from './folderMoves'

/** The top level's drop space in the Tables list (around its loose tables). */
export const TOP_LEVEL_DROP_ID = 'drop-unorganized'

/**
 * The share of a folder's name row, at its top and at its bottom, where a
 * dragged folder from the same level goes beside it; the middle takes it in.
 */
const FOLDER_EDGE_SHARE = 0.25

const spansY = (rect: ClientRect | undefined, y: number) =>
  rect !== undefined && y >= rect.top && y <= rect.bottom

/**
 * Where a folder dragged in the Custom Tables list lands, by the pointer:
 * inside another folder while over the middle of its name; beside a folder at
 * its own level while over the top or bottom of that folder's name, or
 * anywhere else nearest one (the sort strategy makes room); at the top level
 * while over the top level's space, for a folder inside another. Never inside
 * itself or a folder in it, and over its own parent's name it stays beside its
 * siblings. A keyboard drag, with no pointer, moves among its own level.
 */
export function folderDragCollision(folders: readonly FolderSchema[]): CollisionDetection {
  const parentById = new Map(folders.map((folder) => [folder.id, folder.parent_id]))
  return (args) => {
    const active = args.active.data.current
    const activeId = active?.folderId as string
    const parentId = (active?.parentId ?? null) as string | null
    const besideSiblings = (): Collision[] =>
      closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((container) => {
          const data = container.data.current
          return data?.type === 'folder' && (data.parentId ?? null) === parentId
        }),
      })

    const pointer = args.pointerCoordinates
    if (!pointer) return besideSiblings()
    const under = (type: string) =>
      args.droppableContainers.find(
        (container) =>
          container.data.current?.type === type &&
          spansY(args.droppableRects.get(container.id), pointer.y)
      )

    const header = under('folder-drop')
    if (header) {
      const folderId = header.data.current?.folderId as string
      if (isWithinFolder(folders, folderId, activeId)) return [{ id: `folder-${activeId}` }]
      if (folderId === parentId) return besideSiblings()
      const rect = args.droppableRects.get(header.id)!
      const edge = rect.height * FOLDER_EDGE_SHARE
      const nearEdge = pointer.y < rect.top + edge || pointer.y > rect.bottom - edge
      if (nearEdge && (parentById.get(folderId) ?? null) === parentId) {
        return [{ id: `folder-${folderId}` }]
      }
      return [{ id: header.id }]
    }

    if (parentId !== null) {
      const topLevel = under('unorganized-drop')
      if (topLevel) return [{ id: topLevel.id }]
    }
    return besideSiblings()
  }
}

/**
 * Where a table dragged in the Custom Tables list lands: the nearest table
 * row or folder. The top level's own space counts only while no table is at
 * the top level to drop beside (every table is in a folder); otherwise its
 * middle would win over the rows inside it.
 */
export function tableDragCollision(hasTopLevelTables: boolean): CollisionDetection {
  return (args) =>
    closestCenter(
      hasTopLevelTables
        ? {
            ...args,
            droppableContainers: args.droppableContainers.filter(
              (container) => container.data.current?.type !== 'unorganized-drop'
            ),
          }
        : args
    )
}
